import { Link, useNavigate, useRouter, type NavigateOptions } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import { Button, RouterProvider } from "react-aria-components";
import { FormattedMessage } from "react-intl";
import { api } from "../api.ts";
import { centralHref, leaveFor } from "../addresses.ts";
import { configQueryKey, useConfig } from "../config.tsx";
import { withMessages } from "../i18n.ts";
import { unreadQuery } from "../inbox.ts";
import { useEmailConfirmation } from "../reauth-bus.ts";
import { QuickFindHost } from "../workspace/QuickFindHost.tsx";
import { AnnouncerProvider } from "./Announcer.tsx";

// Shown rarely, so loaded only then (ADR-0076).
const EmailPrompts = lazy(withMessages(() => import("./EmailPrompts.tsx")));

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: Omit<NavigateOptions, "to" | "from">;
  }
}

function InboxLink() {
  const unread = useQuery(unreadQuery).data?.count ?? 0;
  // The count is part of the link's text, so its name ("Inbox 2 unread") contains what is shown.
  return (
    <Link to="/inbox" activeProps={{ "aria-current": "page" }}>
      <FormattedMessage id="inbox.nav" />
      {unread > 0 && (
        <>
          {" "}
          <span className="badge">
            {unread}
            <span className="visually-hidden">
              {" "}
              <FormattedMessage id="inbox.unread" />
            </span>
          </span>
        </>
      )}
    </Link>
  );
}

/** A link of the header, to the central host for what only it serves (ADR-0098 §5). */
function HeaderLink(props: {
  to: "/admin/settings" | "/admin/groups" | "/admin/translations" | "/account/security";
  children: ReactNode;
}) {
  const there = centralHref(useConfig(), props.to);
  return there ? (
    <a href={there}>{props.children}</a>
  ) : (
    <Link to={props.to} activeProps={{ "aria-current": "page" }}>
      {props.children}
    </Link>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const config = useConfig();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const confirming = useEmailConfirmation();

  async function signOut() {
    await api("/auth/logout", { method: "POST" });
    // Drop the signed-out user's data so the next user never sees it.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== configQueryKey[0] });
    // Signed out everywhere (ADR-0098 §4): people sign in again at the central host.
    if (config.address) await leaveFor(`${config.centralOrigin}/sign-in`);
    await queryClient.refetchQueries({ queryKey: configQueryKey });
    await navigate({ to: "/sign-in" });
  }

  // React Aria links (tree items, menu items) navigate through the app router, not page loads.
  return (
    <RouterProvider
      navigate={(href, options) => void router.navigate({ ...options, href })}
      useHref={(href) => href}
    >
      <AnnouncerProvider>
        <QuickFindHost>
          <a className="skip-link" href="#main">
            <FormattedMessage id="app.skipToContent" />
          </a>
          <header className="app-header">
            <Link to="/" className="brand">
              {config.appearance.logo && (
                <img
                  className="brand-logo"
                  src={`/api/appearance/logo?v=${config.appearance.version}`}
                  alt={config.appearance.logoAlt}
                />
              )}
              {config.displayName}
            </Link>
            {config.user && (
              <nav aria-label={config.displayName}>
                <ul>
                  <li>
                    <InboxLink />
                  </li>
                  {config.user.isInstanceAdmin && (
                    <li>
                      <HeaderLink to="/admin/settings">
                        <FormattedMessage id="home.admin" />
                      </HeaderLink>
                    </li>
                  )}
                  {!config.user.isInstanceAdmin && config.user.managesGroups && (
                    <li>
                      <HeaderLink to="/admin/groups">
                        <FormattedMessage id="home.groups" />
                      </HeaderLink>
                    </li>
                  )}
                  {!config.user.isInstanceAdmin && config.user.translates.length > 0 && (
                    <li>
                      <HeaderLink to="/admin/translations">
                        <FormattedMessage id="home.translations" />
                      </HeaderLink>
                    </li>
                  )}
                  <li>
                    <HeaderLink to="/account/security">
                      <FormattedMessage id="security.nav" />
                    </HeaderLink>
                  </li>
                  <li>
                    <Button className="button button-quiet" onPress={() => void signOut()}>
                      <FormattedMessage id="auth.signOut" />
                    </Button>
                  </li>
                </ul>
              </nav>
            )}
          </header>
          {(confirming || (config.user && config.auth.signedInWith === "email")) && (
            <Suspense fallback={null}>
              <EmailPrompts
                // Passkeys are added at the central host only (ADR-0098 §4).
                offer={config.auth.signedInWith === "email" && !config.address}
                confirming={confirming}
              />
            </Suspense>
          )}
          {children}
        </QuickFindHost>
      </AnnouncerProvider>
    </RouterProvider>
  );
}
